import { Box, styled } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import FlexBox from 'Components/FlexBox';
import ThemeTypography from 'DesignSystem/Primitives/Typography/ThemeTypography';
import {
  EQuestionnaireRiskLevel,
  TQuestionnaireRiskLevel,
} from 'FeatureModules/Questionnaire/types/questionnaire.types';
import React, { FC, useMemo } from 'react';
import theme from 'styles/theme';
import RiskPieLabel from './RiskPieChartLabel';

interface IRiskPieChartProps {
  inProgressAmount?: number;
  lowRiskAmount: number;
  mediumRiskAmount: number;
  highRiskAmount: number;
  height?: number;
  hideLegendAmounts?: boolean;
  amountLabel?: string;
}

const RISK_COLOR: Record<TQuestionnaireRiskLevel, string> = {
  [EQuestionnaireRiskLevel.LOW]: theme.custom.themeColors.success[100],
  [EQuestionnaireRiskLevel.MEDIUM]: theme.custom.themeColors.accent[100],
  [EQuestionnaireRiskLevel.HIGH]: theme.custom.themeColors.error[80],
  [EQuestionnaireRiskLevel.PENDING]: theme.custom.colors.lightViolet,
};

const Container = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.custom.themeColors.grayScale[20]}`,
  background: theme.custom.themeColors.white,
  width: '100%',
  '& .MuiPieArc-root': {
    strokeWidth: 0,
  },
  borderRadius: 6,
  height: 362, // Match the height with the map plot overview
}));

const Amount = styled('span')(({ theme }) => ({
  color: theme.custom.themeColors.grayScale[60],
}));

const RiskBadge = styled(Box)<{ riskLevel: EQuestionnaireRiskLevel }>(({ theme, riskLevel }) => ({
  position: 'relative',
  backgroundColor: theme.custom.themeColors.primary[5],
  borderRadius: 20,
  padding: theme.spacing(0.5, 2, 0.5, 3.5),
  display: 'flex',
  alignItems: 'center',
  '&::before': {
    content: '""',
    display: 'block',
    width: '13px',
    height: '13px',
    borderRadius: '50%',
    backgroundColor: RISK_COLOR[riskLevel],
    position: 'absolute',
    top: '50%',
    left: theme.spacing(2),
    transform: 'translate(-50%, -50%)',
  },
}));

const RiskPieChart: FC<IRiskPieChartProps> = ({
  inProgressAmount,
  lowRiskAmount,
  mediumRiskAmount,
  highRiskAmount,
  hideLegendAmounts,
  amountLabel = 'item',
  height = 230,
}) => {
  /**
   * Sorted list of risk levels to be shown in the legend, sorted by amount descending
   */
  const sortedData = useMemo(() => {
    const total = lowRiskAmount + mediumRiskAmount + highRiskAmount + (inProgressAmount || 0);
    return [
      {
        label: 'Low risk',
        value: lowRiskAmount,
        color: RISK_COLOR[EQuestionnaireRiskLevel.LOW],
        riskLevel: EQuestionnaireRiskLevel.LOW,
        percentage: lowRiskAmount === 0 ? '0' : ((lowRiskAmount / total) * 100).toFixed(0),
      },
      {
        label: 'Medium risk',
        value: mediumRiskAmount,
        color: RISK_COLOR[EQuestionnaireRiskLevel.MEDIUM],
        riskLevel: EQuestionnaireRiskLevel.MEDIUM,
        percentage: mediumRiskAmount === 0 ? '0' : ((mediumRiskAmount / total) * 100).toFixed(0),
      },
      {
        label: 'High risk',
        value: highRiskAmount,
        color: RISK_COLOR[EQuestionnaireRiskLevel.HIGH],
        riskLevel: EQuestionnaireRiskLevel.HIGH,
        percentage: highRiskAmount === 0 ? '0' : ((highRiskAmount / total) * 100).toFixed(0),
      },
      {
        label: 'In progress',
        value: inProgressAmount || 0,
        color: RISK_COLOR[EQuestionnaireRiskLevel.PENDING],
        riskLevel: EQuestionnaireRiskLevel.PENDING,
        percentage: inProgressAmount ? ((inProgressAmount / total) * 100).toFixed(0) : '0',
      },
    ].sort((a, b) => b.value - a.value);
  }, [lowRiskAmount, inProgressAmount, mediumRiskAmount, highRiskAmount]);

  const highestValue = useMemo(
    () =>
      sortedData.reduce(
        (prev, item) => (prev && prev.value > item.value ? prev : item),
        sortedData[0]
      ),
    [sortedData]
  );

  return (
    <Container
      display="flex"
      alignItems="center"
      flexDirection={{ xs: 'column', lg: 'row' }}
      gap={2}
      padding={{ xs: 2, lg: 4, xl: 5 }}
    >
      <Box width="fit-content">
        <PieChart
          series={[
            {
              data: sortedData,
              innerRadius: height / 3.5,
            },
          ]}
          margin={{ right: 0, left: 0, top: 0, bottom: 0 }}
          width={height}
          height={height}
          slotProps={{
            legend: { hidden: true },
          }}
          tooltip={{ trigger: 'none' }}
        >
          <RiskPieLabel label={`${highestValue.percentage}%`} subLabel={highestValue.label} />
        </PieChart>
      </Box>

      {/* Legend */}
      <FlexBox flexDirection="column" alignItems="flex-start" mb={2} gap={1}>
        {sortedData.map(({ riskLevel, value, percentage, label }) => (
          <RiskBadge key={riskLevel} riskLevel={riskLevel}>
            <ThemeTypography variant="BUTTON_SMALL">
              {percentage}% {label}{' '}
              {!hideLegendAmounts && (
                <Amount>
                  ({value} {value && Number(value) === 1 ? amountLabel : `${amountLabel}s`})
                </Amount>
              )}
            </ThemeTypography>
          </RiskBadge>
        ))}
      </FlexBox>
    </Container>
  );
};

export default RiskPieChart;
